import {
  Component,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  Renderer2,
} from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
// Ag-Grid
import {
  GridOptions,
  GridApi,
  ColDef,
  CellContextMenuEvent,
  RowSelectionOptions,
} from 'ag-grid-community';
import { agGridDarkTheme } from 'src/app/shared/components/ag-grid/dark-theme';
import { AsyncPipe } from '@angular/common';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { FormsModule } from '@angular/forms';
// Services
import { SharedService } from '../../shared/services/shared.service';
import { ContextMenuService } from '../../shared/services/context-menu.service';
// Third Party
import { Subscription } from 'rxjs';
import { format } from 'date-fns';

// components
import { BoundBookBtnsComponent } from 'src/app/shared/components/ag-grid/bound-book-btns/bound-book-btns.component';
import { StateBadgeComponent } from 'src/app/shared/components/ag-grid/state-badge/state-badge.component';
// models
import { BoundBookColumnDef } from './columnDefs';
import { BoundBookEntry } from 'src/app/shared/models/general';
import { BoundBookList } from './models';
import {
  ComponentNames,
  DateFormats,
  EmailType,
  ModalID,
} from 'src/app/shared/models/constants';
import { AgGridService } from 'src/app/shared/services/ag-grid.service';
import { isObjectEmpty } from 'src/app/shared/functions';

@Component({
  selector: 'app-bound-book',
  templateUrl: './bound-book.component.html',
  styleUrls: ['./bound-book.component.scss'],
  standalone: true,
  imports: [AgGridAngular, AsyncPipe, FormsModule, BsDropdownModule],
})
export class BoundBookComponent {
  @ViewChild('grid', { read: ElementRef }) gridElement!: ElementRef;
  @ViewChild('grid') grid!: AgGridAngular; // Keep for grid API access
  subscriptions = new Subscription();
  // implements OnDestroy
  rowData: any[] = [];
  selected_row: BoundBookEntry;
  isEditingName = false;
  tempName: string = '';

  public rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    enableSelectionWithoutKeys: true,
    checkboxes: false,
  };
  // ag-grid properties Grid
  gridApi!: GridApi;

  columnDefs: ColDef[] = BoundBookColumnDef;
  defaultColDef;
  gridOptions: GridOptions = {
    theme: agGridDarkTheme,
    suppressDragLeaveHidesColumns: true,
  };
  components: any;
  theme = agGridDarkTheme;
  // Context Menu state from service
  menuState$ = this.contextMenuService.menuState$;

  constructor(
    public sharedService: SharedService,
    private contextMenuService: ContextMenuService,
    private agGridService: AgGridService,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
  ) {
    this.gridOptions = <GridOptions>{};
    this.gridOptions.headerHeight = 50;
    this.gridOptions.rowHeight = 28;
    this.gridOptions.enableCellTextSelection = true; // Allows text selection for copying
    this.gridOptions.ensureDomOrder = true; // Ensures proper DOM order for accessibility
    this.gridOptions.suppressClipboardPaste = false; // Allow clipboard operations
    this.gridOptions.context = { ConfigComponent: this }; // this allows access to component functions
    this.gridOptions.rowClass = 'ag-grid-row';
    this.defaultColDef = {
      sortable: true,
      filter: true,
      resizable: true,
      floatingFilter: true,
      wrapHeaderText: true,
      floatingFilterComponentParams: { placeholder: 'Search...' },
    };
    this.components = {
      BoundBookOptionBtns: BoundBookBtnsComponent,
      StateBadge: StateBadgeComponent,
    };
    this.subscriptions.add(
      this.sharedService.bound_book_retrieved$.subscribe((response) => {
        if (this.rowData.length === 0) {
          this.createBoundBookList();
        }
      }),
    );
    this.subscriptions.add(
      this.sharedService.bound_book_updated$.subscribe((bound_book) => {
        this.createBoundBookList();
      }),
    );
    this.subscriptions.add(
      this.sharedService.boundbook_selected$.subscribe((bound_book) => {
        this.createBoundBookList();
      }),
    );
    // // ag-grid functions boundbook_selected
    //this.gridOptions.getContextMenuItems = getContextMenuItems;
  }

  async onGridReady(params: any) {
    this.gridApi = params.api;

    if (!this.agGridService.hasCachedSettingsFor(ComponentNames.bound_book)) {
      await this.agGridService.loadSavedTableSettings(
        ComponentNames.bound_book,
      );
    }
    this.applyCurrentSessionPreferences();
    setTimeout(() => this.updateFloatingFilterHighlights(), 100);
  }
  async ngOnInit(): Promise<void> {
    this.createBoundBookList();
    this.addButtonColumn();
    this.sharedService.getPending3310Records();
    if (!this.sharedService.bound_book || this.sharedService.bound_book.length === 0) {
      await this.refreshTableDataClick();
    }
    this.subscriptions.add(
      this.sharedService.email_sent$.subscribe((evt) => {
        if (evt.email_type === EmailType.form_3310 && evt.pending3310_id) {
          this.sharedService.deletePending3310AndMarkSent(evt.pending3310_id).then(() => {
            this.createBoundBookList();
          });
        }
      }),
    );
  }
  ngAfterViewInit() {
    this.contextMenuService.initialize(this.cdr, this.gridElement);
  }
  private updateFloatingFilterHighlights(): void {
    if (!this.gridApi || !this.gridElement || !this.gridElement.nativeElement) {
      return;
    }
    const filterModel = this.gridApi.getFilterModel() || {};
    const activeCols = new Set(Object.keys(filterModel));

    // Find all inputs that might belong to floating filters
    const inputs: NodeListOf<HTMLInputElement> =
      this.gridElement.nativeElement.querySelectorAll(
        '.ag-floating-filter input, .ag-header-row-column-filter input',
      );

    inputs.forEach((input) => {
      if (input.type === 'checkbox') return;

      const cell = input.closest('[col-id]');
      const colId = cell ? cell.getAttribute('col-id') : '';

      // Check if it's active based on the filter model OR a direct value presence
      const isActive =
        (colId && activeCols.has(colId)) ||
        (input.value && input.value.trim() !== '');

      // Apply visual active class to the wrapper (preferred in Quartz) or the input
      const wrapper =
        input.closest('.ag-input-wrapper') || input.closest('.ag-wrapper');
      const targetElement = wrapper || input;

      if (isActive) {
        this.renderer.addClass(targetElement, 'active-filter-input');
      } else {
        this.renderer.removeClass(targetElement, 'active-filter-input');
      }
    });
  }

  addButtonColumn() {
    const hasViewDetails = this.columnDefs.some(
      (column) => column.headerName === 'View Details',
    );
    if (!hasViewDetails) {
      this.columnDefs.push({
        // View Details
        headerName: 'View Details',
        field: 'view_details',
        resizable: true,
        width: 140,
        pinned: 'right',
        cellRenderer: 'BoundBookOptionBtns',
        cellRendererParams: {
          viewFirearmDetails: this.viewFirearmDetailsClicked.bind(this),
          deleteFirearm: this.deleteFirearmClicked.bind(this),
          printPackingSlip: this.printPackingSlip.bind(this),
          send3310Email: this.send3310EmailClicked.bind(this),
        },
      });
    }
  }
  async refreshTableDataClick() {
    this.sharedService.openStatusSpinner('Refreshing Bound Book Records');
    await this.sharedService.getBoundBookEntries();
    this.createBoundBookList();
    this.sharedService.closeStatusSpinner();
    setTimeout(() => this.updateFloatingFilterHighlights(), 500);
  }
  viewFirearmDetailsClicked(selected_row: {
    colValues: BoundBookList;
    rowIndex: number;
  }) {
    const bb_entry = this.sharedService.getSingleBoundBookEntry(
      selected_row['colValues']['firearm_id'],
    );
    this.sharedService.openFirearmInfoDash(bb_entry);
  }
  deleteFirearmClicked() {
    console.log('delete firearm clicked');
  }
  send3310EmailClicked(selected_row: { colValues: BoundBookList; rowIndex: number }) {
    const row = selected_row['colValues'];
    if (!row.form_4473_transfer_number || !row.has_pending_3310) return;
    const pending3310_id = row.pending3310_id;
    this.sharedService.getForm4473ByTransactionSerial(row.form_4473_transfer_number).then((form4473) => {
      if (form4473?._id) {
        this.sharedService.generate3310AndOpenEmailModal(form4473, pending3310_id);
      }
    });
  }
  printPackingSlip(selected_row: {
    colValues: BoundBookList;
    rowIndex: number;
  }) {
    console.warn(selected_row);
    const related_disposition_items: BoundBookEntry[] =
      this.sharedService.bound_book.filter(
        (bb_entry) =>
          bb_entry.firearm_id === selected_row['colValues']['firearm_id'],
      );
    this.sharedService.openPackingSlipPrintModal(related_disposition_items);
  }
  createBoundBookList() {
    this.rowData = [];
    if (
      this.sharedService.bound_book &&
      this.sharedService.bound_book.length > 0 &&
      this.sharedService.selected_boundbook.boundbook_id
    ) {
      for (const bb_entry of this.sharedService.bound_book) {
        const bb_item = new BoundBookList();
        bb_item.bound_book_id =
          this.sharedService.selected_boundbook.boundbook_id;
        bb_item.date_acquired = bb_entry.a_date_acquired
          ? format(bb_entry.a_date_acquired, DateFormats.M_D_Y)
          : 'N/A';
        bb_item.firearm_id = bb_entry.firearm_id;
        bb_item.received_from = bb_entry.a_received_from_name;
        bb_item.serial_number = bb_entry.a_serial_number;
        bb_item.state = bb_entry.state.substring(0, 1);
        bb_item.select_item = false;
        bb_item.transfered_to = this.getTransferedToName(bb_entry);
        bb_item.date_disposed = bb_entry.d_date_disposed
          ? format(bb_entry.d_date_disposed, DateFormats.M_D_Y)
          : '';
        bb_item.d_id = bb_entry.d_id;
        bb_item.form_4473_transfer_number = bb_entry.d_form4473_id;
        // Check if this 4473 has a pending 3310 reminder
        const pendingRecord = bb_entry.d_form4473_id
          ? this.sharedService.pending_3310_records.find(
              (r) => r.form4473_id?.toString() === bb_entry.d_form4473_id?.toString(),
            )
          : null;
        bb_item.has_pending_3310 = !!pendingRecord;
        bb_item.pending3310_id = pendingRecord?._id || null;
        bb_item.firearm = `${bb_entry.a_manufacture} - ${bb_entry.a_model} - ${bb_entry.a_caliber_gauge}`;
        this.rowData.push(bb_item);
      }
      this.rowData = [...this.rowData.sort((a, b) => b.firearm_id - a.firearm_id)];
    }
  }
  createBoundBookPrintableClick() {
    this.sharedService.openBoundBookPrintModal(this.sharedService.bound_book);
  }
  startEditName() {
    this.tempName = this.sharedService.selected_boundbook.boundbook_name;
    this.isEditingName = true;
  }

  async saveName() {
    if (
      this.tempName.trim() &&
      this.tempName !== this.sharedService.selected_boundbook.boundbook_name
    ) {
      await this.sharedService.renameBoundBook(
        this.sharedService.selected_boundbook.boundbook_id,
        this.tempName,
      );
    }
    this.isEditingName = false;
  }

  cancelEdit() {
    this.isEditingName = false;
  }

  getTransferedToName(bb_entry: BoundBookEntry) {
    if (bb_entry.d_disposed_to) {
      return bb_entry.d_business_name
        ? bb_entry.d_business_name
        : bb_entry.d_disposed_to;
    } else {
      return `${bb_entry.d_first_name} ${bb_entry.d_middle_name} ${bb_entry.d_last_name}`;
    }
  }
  openHome() {
    this.sharedService.changeRoute('home');
  }
  createAcquisitionClick() {
    this.sharedService.changeRoute('acquisition');
  }
  createBulkAcquisitionClick() {
    this.sharedService.changeRoute('acquisition-bulk');
  }
  createDispositionClick() {
    this.sharedService.changeRoute('disposition');
  }

  // right click copy functions
  onCellContextMenu(event: CellContextMenuEvent) {
    this.contextMenuService.onCellContextMenu(event);
  }
  onMenuMouseEnter() {
    this.contextMenuService.onMenuMouseEnter();
  }
  onMenuMouseLeave() {
    this.contextMenuService.onMenuMouseLeave();
  }
  copyCellValue() {
    this.contextMenuService.copyCellValue();
  }

  // preference functions
  storeCurrentSessionFilters() {
    this.agGridService.storeCurrentSessionFilters(
      ComponentNames.bound_book,
      this.gridApi.getFilterModel(),
    );
    setTimeout(() => this.updateFloatingFilterHighlights(), 100);
  }
  storeCurrentSessionColumnConfig() {
    this.agGridService.storeCurrentSessionColumnConfig(
      ComponentNames.bound_book,
      this.gridApi?.getColumnState(),
    );
  }
  applyCurrentSessionPreferences() {
    let component: any = this.agGridService.current_session_filtering.find(
      (component) => component.component_name === ComponentNames.bound_book,
    );
    if (component && !isObjectEmpty(component.filters)) {
      this.gridApi.setFilterModel(component.filters);
    }
    component = this.agGridService.current_session_column_config.find(
      (component) => component.component_name === ComponentNames.bound_book,
    );
    if (component && !isObjectEmpty(component.column_config)) {
      this.gridApi?.applyColumnState({
        state: component.column_config,
        applyOrder: true,
      });
    }
    setTimeout(() => this.updateFloatingFilterHighlights(), 100);
  }
  saveCurrentTablePreferencesClick() {
    const filters = this.gridApi.getFilterModel();
    const column_state = this.gridApi?.getColumnState();
    this.agGridService.saveCurrentTableSettings(
      ComponentNames.bound_book,
      filters,
      column_state,
    );
  }
}

